import { jsPDF } from 'jspdf'
import { applicabilityLabel } from '../assessment/applicability'
import type { ReportData } from '../assessment/types'

/**
 * Consulting-style PDF (McKinsey / BCG inspired):
 * restrained charcoal + single brand accent, editorial cover,
 * numbered sections, thin rules, KPI strip, matrix, clean tables.
 */
const C = {
  ink: [26, 32, 44] as [number, number, number],
  body: [45, 55, 72] as [number, number, number],
  mute: [113, 128, 150] as [number, number, number],
  line: [226, 232, 240] as [number, number, number],
  soft: [247, 250, 252] as [number, number, number],
  accent: [11, 110, 79] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  cover: [15, 23, 42] as [number, number, number],
}

const MARGIN_X = 20
const PAGE_W = 210
const PAGE_H = 297
const CONTENT_W = PAGE_W - MARGIN_X * 2
const TOP = 24
const BOTTOM = 276

type Doc = jsPDF
type Rgb = [number, number, number]

function setInk(doc: Doc, rgb: Rgb = C.ink): void {
  doc.setTextColor(rgb[0], rgb[1], rgb[2])
}

function setFill(doc: Doc, rgb: Rgb): void {
  doc.setFillColor(rgb[0], rgb[1], rgb[2])
}

function setDraw(doc: Doc, rgb: Rgb): void {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2])
}

function hLine(doc: Doc, y: number, x = MARGIN_X, w = CONTENT_W, rgb: Rgb = C.line): void {
  setDraw(doc, rgb)
  doc.setLineWidth(0.25)
  doc.line(x, y, x + w, y)
}

function ensure(doc: Doc, y: number, need: number, onNewPage: () => void): number {
  if (y + need <= BOTTOM) return y
  doc.addPage()
  onNewPage()
  return TOP + 6
}

function textBlock(
  doc: Doc,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lineH: number,
  onNewPage: () => void,
): number {
  const lines = doc.splitTextToSize(text, maxW) as string[]
  for (const line of lines) {
    y = ensure(doc, y, lineH + 1, onNewPage)
    doc.text(line, x, y)
    y += lineH
  }
  return y
}

function sectionTitle(
  doc: Doc,
  index: string,
  title: string,
  y: number,
  onNewPage: () => void,
): number {
  y = ensure(doc, y, 20, onNewPage)
  y += 6
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  setInk(doc, C.accent)
  doc.text(index, MARGIN_X, y)
  doc.setFont('times', 'bold')
  doc.setFontSize(14)
  setInk(doc, C.ink)
  doc.text(title, MARGIN_X + 12, y)
  y += 3
  hLine(doc, y, MARGIN_X, CONTENT_W, C.line)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  setInk(doc, C.body)
  return y + 8
}

function drawPageChrome(
  doc: Doc,
  brand: string,
  company: string,
  pageNo: number,
  total: number,
  isCover: boolean,
): void {
  if (isCover) return

  // Left accent rail
  setFill(doc, C.accent)
  doc.rect(0, 0, 2.2, PAGE_H, 'F')

  // Header
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  setInk(doc, C.accent)
  doc.text(brand.toUpperCase(), MARGIN_X, 12)
  doc.setFont('helvetica', 'normal')
  setInk(doc, C.mute)
  const headerRight = company.length > 42 ? `${company.slice(0, 40)}…` : company
  doc.text(headerRight, PAGE_W - MARGIN_X, 12, { align: 'right' })
  hLine(doc, 15)

  // Footer
  hLine(doc, 283)
  doc.setFontSize(7)
  setInk(doc, C.mute)
  doc.text('Preliminary self-assessment · Not legal advice or a verified inventory', MARGIN_X, 289)
  doc.text(`${pageNo}  |  ${total}`, PAGE_W - MARGIN_X, 289, { align: 'right' })
}

function kpiBox(
  doc: Doc,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  value: string,
): void {
  setFill(doc, C.soft)
  doc.rect(x, y, w, h, 'F')
  setFill(doc, C.accent)
  doc.rect(x, y, 1.6, h, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  setInk(doc, C.mute)
  doc.text(label.toUpperCase(), x + 5, y + 6)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  setInk(doc, C.ink)
  doc.text(value, x + 5, y + 16)
}

function tableHeader(doc: Doc, y: number, cols: Array<{ label: string; x: number }>): number {
  setFill(doc, C.soft)
  doc.rect(MARGIN_X, y - 4.5, CONTENT_W, 8.5, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  setInk(doc, C.mute)
  for (const col of cols) doc.text(col.label.toUpperCase(), col.x, y)
  hLine(doc, y + 3.5)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  setInk(doc, C.body)
  return y + 9
}

function drawMatrix(
  doc: Doc,
  y: number,
  points: Array<{ name: string; fx: number; iy: number }>,
  onNewPage: () => void,
): number {
  const size = 80
  y = ensure(doc, y, size + 22, onNewPage)
  const x0 = MARGIN_X + 10
  const y0 = y

  // Soft quadrant fills
  setFill(doc, [236, 253, 245])
  doc.rect(x0 + size / 2, y0, size / 2, size / 2, 'F') // strategic (top-right)
  setFill(doc, C.soft)
  doc.rect(x0, y0, size / 2, size / 2, 'F') // monitor
  doc.rect(x0 + size / 2, y0 + size / 2, size / 2, size / 2, 'F') // financial focus
  setFill(doc, C.white)
  doc.rect(x0, y0 + size / 2, size / 2, size / 2, 'F') // lower

  setDraw(doc, C.line)
  doc.setLineWidth(0.4)
  doc.rect(x0, y0, size, size)
  doc.setLineWidth(0.2)
  doc.line(x0 + size / 2, y0, x0 + size / 2, y0 + size)
  doc.line(x0, y0 + size / 2, x0 + size, y0 + size / 2)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6)
  setInk(doc, C.mute)
  doc.text('MONITOR', x0 + 3, y0 + 5)
  doc.text('STRATEGIC', x0 + size - 22, y0 + 5)
  doc.text('LOWER', x0 + 3, y0 + size - 3)
  doc.text('FINANCIAL FOCUS', x0 + size - 32, y0 + size - 3)

  for (const p of points.slice(0, 16)) {
    const px = x0 + ((p.fx - 1) / 4) * size
    const py = y0 + size - ((p.iy - 1) / 4) * size
    setFill(doc, C.accent)
    doc.circle(px, py, 1.4, 'F')
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  setInk(doc, C.mute)
  doc.text('Business effect →', x0, y0 + size + 6)
  doc.text('People & environment effect →', x0 - 2, y0 + size / 2 + 18, { angle: 90 })

  let ly = y0 + 3
  const lx = x0 + size + 12
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  setInk(doc, C.ink)
  doc.text('Plotted topics', lx, ly)
  ly += 6
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  for (const p of points.slice(0, 12)) {
    ly = ensure(doc, ly, 5, onNewPage)
    setFill(doc, C.accent)
    doc.circle(lx + 1.5, ly - 1.2, 1.1, 'F')
    setInk(doc, C.body)
    const label = doc.splitTextToSize(`${p.name}  (${p.fx}, ${p.iy})`, 68) as string[]
    doc.text(label[0], lx + 5, ly)
    ly += 4.8
  }

  return Math.max(y0 + size + 14, ly + 2)
}

const SECTOR_LABELS: Record<string, string> = {
  manufacturing: 'Manufacturing',
  construction: 'Construction and engineering',
  real_estate: 'Real estate',
  logistics: 'Logistics',
  oil_gas_energy: 'Oil, gas and energy',
  hospitality: 'Hospitality',
  retail: 'Retail and wholesale',
  healthcare: 'Healthcare',
  financial_services: 'Financial services',
  technology_services: 'Technology and professional services',
  agriculture: 'Agriculture',
  other: 'Other',
}

function pretty(value: string): string {
  return value.replace(/_/g, ' ')
}

export async function generateAssessmentPdf(data: ReportData): Promise<Blob> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const { result } = data
  const profile = result.companyProfile
  const company = profile.companyName?.trim() || 'Company name not provided'
  const generated = new Date(result.generatedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const paintHeader = () => {
    /* chrome applied in post-pass */
  }

  // ═══════════════════════════════════════════════════════════
  // COVER
  // ═══════════════════════════════════════════════════════════
  setFill(doc, C.cover)
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F')
  setFill(doc, C.accent)
  doc.rect(0, 0, 7, PAGE_H, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  setInk(doc, C.accent)
  doc.text(data.brandName.toUpperCase(), MARGIN_X + 6, 32)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  setInk(doc, [148, 163, 184])
  doc.text('CONFIDENTIAL  ·  PRELIMINARY SELF-ASSESSMENT', MARGIN_X + 6, 40)

  doc.setFont('times', 'bold')
  doc.setFontSize(26)
  setInk(doc, C.white)
  doc.text('UAE Sustainability &', MARGIN_X + 6, 78)
  doc.text('GHG Readiness', MARGIN_X + 6, 90)
  doc.text('Assessment', MARGIN_X + 6, 102)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  setInk(doc, [203, 213, 225])
  doc.text('Findings, priorities and a 30 to 90 day action plan', MARGIN_X + 6, 116)

  setDraw(doc, [51, 65, 85])
  doc.setLineWidth(0.4)
  doc.line(MARGIN_X + 6, 128, MARGIN_X + 70, 128)

  doc.setFont('times', 'bold')
  doc.setFontSize(18)
  setInk(doc, C.white)
  doc.text(company, MARGIN_X + 6, 148)

  const facts: Array<[string, string]> = [
    ['Sector', SECTOR_LABELS[profile.sector || ''] || profile.sector || 'Not provided'],
    ['Emirate', profile.emirate || 'Not provided'],
    ['Company size', pretty(profile.companySize || 'Not provided')],
    ['Operating footprint', pretty(profile.operatingFootprint || 'Not provided')],
    ['Listing status', pretty(profile.listingStatus || 'Not provided')],
    ['Method version', result.methodologyVersion],
    ['Generated', generated],
    ['Prepared for', 'Internal discussion'],
  ]

  const fx = MARGIN_X + 6
  const fy = 164
  for (let i = 0; i < facts.length; i++) {
    const [label, value] = facts[i]
    const col = i % 2
    const row = Math.floor(i / 2)
    const x = fx + col * 88
    const yy = fy + row * 16
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    setInk(doc, [148, 163, 184])
    doc.text(label.toUpperCase(), x, yy)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    setInk(doc, C.white)
    const vLines = doc.splitTextToSize(String(value), 82) as string[]
    doc.text(vLines[0], x, yy + 5)
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  setInk(doc, [148, 163, 184])
  const coverNote =
    'This document summarises a browser-based self-assessment. It is intended to support internal prioritisation. It is not a compliance certificate, verified inventory or assurance opinion.'
  const noteLines = doc.splitTextToSize(coverNote, CONTENT_W - 10) as string[]
  let ny = 242
  for (const line of noteLines) {
    doc.text(line, MARGIN_X + 6, ny)
    ny += 4
  }

  doc.setFontSize(8)
  setInk(doc, [148, 163, 184])
  doc.text(`${data.brandName}  ·  ${data.contactEmail}`, MARGIN_X + 6, 280)

  // ═══════════════════════════════════════════════════════════
  // BODY
  // ═══════════════════════════════════════════════════════════
  doc.addPage()
  let y = TOP + 6

  // 01 Executive summary
  y = sectionTitle(doc, '01', 'Executive summary', y, paintHeader)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  setInk(doc, C.body)
  y = textBlock(doc, result.summary, MARGIN_X, y, CONTENT_W, 4.6, paintHeader)
  y += 7

  const kpis = [
    ['Overall readiness', result.overallReadinessPercent == null ? 'N/A' : `${result.overallReadinessPercent}%`],
    ['Evidence confidence', result.evidenceConfidencePercent == null ? 'N/A' : `${result.evidenceConfidencePercent}%`],
    ['Priority topics', String(result.topPriorityTopics.length)],
    ['Uncertain answers', String(result.notSureCount)],
  ]
  y = ensure(doc, y, 28, paintHeader)
  const boxW = (CONTENT_W - 9) / 4
  kpis.forEach((k, i) => kpiBox(doc, MARGIN_X + i * (boxW + 3), y, boxW, 22, k[0], k[1]))
  y += 30
  doc.setFontSize(8)
  setInk(doc, C.mute)
  y = textBlock(doc, result.overallReadinessExplanation, MARGIN_X, y, CONTENT_W, 3.8, paintHeader)
  y += 3
  y = textBlock(doc, result.evidenceConfidenceLabel, MARGIN_X, y, CONTENT_W, 3.8, paintHeader)

  // 02 Company snapshot
  y = sectionTitle(doc, '02', 'Company snapshot', y, paintHeader)
  const snap = [
    ['Sector', SECTOR_LABELS[profile.sector || ''] || profile.sector || 'Not provided'],
    ['Emirate', profile.emirate || 'Not provided'],
    ['Size', pretty(profile.companySize || 'Not provided')],
    ['Footprint', pretty(profile.operatingFootprint || 'Not provided')],
    ['Listing', pretty(profile.listingStatus || 'Not provided')],
    ['Activities', (profile.businessActivities || []).map(pretty).join(', ') || 'Not provided'],
  ]
  y = tableHeader(doc, y, [
    { label: 'Attribute', x: MARGIN_X + 2 },
    { label: 'Detail', x: MARGIN_X + 42 },
  ])
  for (const [label, value] of snap) {
    y = ensure(doc, y, 8, paintHeader)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    setInk(doc, C.mute)
    doc.text(label.toUpperCase(), MARGIN_X + 2, y)
    doc.setFont('helvetica', 'normal')
    setInk(doc, C.body)
    const lines = doc.splitTextToSize(String(value), CONTENT_W - 46) as string[]
    doc.text(lines[0], MARGIN_X + 42, y)
    y += 6.5
    hLine(doc, y - 3)
  }

  // 03 What matters most
  y = sectionTitle(doc, '03', 'What matters most', y, paintHeader)
  doc.setFontSize(9)
  setInk(doc, C.mute)
  y = textBlock(
    doc,
    'Business effect (horizontal) versus people and environment effect (vertical). Ratings come from plain-language questions answered in the assessment. Specialists often call these financial and impact materiality.',
    MARGIN_X,
    y,
    CONTENT_W,
    4.2,
    paintHeader,
  )
  y += 5

  const plotted = result.topicResults
    .filter((t) => t.financialScore != null && t.impactScore != null)
    .map((t) => ({
      name: t.name,
      fx: t.financialScore as number,
      iy: t.impactScore as number,
    }))
  y = drawMatrix(doc, y, plotted, paintHeader)

  y = ensure(doc, y, 22, paintHeader)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  setInk(doc, C.ink)
  doc.text('Highest-priority topics', MARGIN_X, y)
  y += 6
  y = tableHeader(doc, y, [
    { label: '#', x: MARGIN_X + 2 },
    { label: 'Topic', x: MARGIN_X + 12 },
    { label: 'Business', x: MARGIN_X + 98 },
    { label: 'People / env.', x: MARGIN_X + 126 },
    { label: 'Confidence', x: MARGIN_X + 156 },
  ])
  for (const t of result.topPriorityTopics) {
    y = ensure(doc, y, 8, paintHeader)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    setInk(doc, C.body)
    doc.text(String(t.priorityRank), MARGIN_X + 2, y)
    const nameLines = doc.splitTextToSize(t.name, 82) as string[]
    doc.text(nameLines[0], MARGIN_X + 12, y)
    doc.text(t.financialLabel, MARGIN_X + 98, y)
    doc.text(t.impactLabel, MARGIN_X + 126, y)
    doc.text(t.confidence, MARGIN_X + 156, y)
    y += 7
    hLine(doc, y - 3)
  }

  // 04 Carbon
  y = sectionTitle(doc, '04', 'Carbon inventory readiness', y, paintHeader)
  doc.setFontSize(9)
  setInk(doc, C.mute)
  y = textBlock(
    doc,
    'Process readiness by GHG scope. This is not an emissions calculation. Missing data is not treated as zero emissions.',
    MARGIN_X,
    y,
    CONTENT_W,
    4.2,
    paintHeader,
  )
  y += 5
  for (const scope of result.carbonByScope) {
    y = ensure(doc, y, 22, paintHeader)
    setFill(doc, C.soft)
    doc.rect(MARGIN_X, y - 4, CONTENT_W, 13, 'F')
    setFill(doc, C.accent)
    doc.rect(MARGIN_X, y - 4, 1.6, 13, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    setInk(doc, C.ink)
    doc.text(`Scope ${scope.scope}`, MARGIN_X + 6, y + 4)
    doc.setFontSize(13)
    setInk(doc, C.accent)
    doc.text(scope.scorePercent == null ? 'N/A' : `${scope.scorePercent}%`, PAGE_W - MARGIN_X - 4, y + 4, {
      align: 'right',
    })
    y += 14
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    setInk(doc, C.mute)
    y = textBlock(
      doc,
      `${scope.answeredCount} of ${scope.applicableCount} applicable sources assessed; ${scope.unansweredCount} unanswered.`,
      MARGIN_X,
      y,
      CONTENT_W,
      3.8,
      paintHeader,
    )
    doc.setFontSize(8.5)
    setInk(doc, C.body)
    for (const gap of scope.gaps.slice(0, 4)) {
      y = textBlock(doc, `-  ${gap}`, MARGIN_X, y, CONTENT_W, 3.8, paintHeader)
    }
    y += 5
  }

  // 05 Gaps
  y = sectionTitle(doc, '05', 'Evidence and process gaps', y, paintHeader)
  y = tableHeader(doc, y, [
    { label: 'Severity', x: MARGIN_X + 2 },
    { label: 'Area', x: MARGIN_X + 30 },
    { label: 'Finding', x: MARGIN_X + 72 },
  ])
  for (const gap of result.evidenceGaps.slice(0, 14)) {
    y = ensure(doc, y, 10, paintHeader)
    doc.setFontSize(8)
    setInk(doc, C.body)
    doc.text(gap.severity, MARGIN_X + 2, y)
    doc.text(gap.area.slice(0, 22), MARGIN_X + 30, y)
    y = textBlock(doc, gap.description, MARGIN_X + 72, y, CONTENT_W - 72, 3.8, paintHeader)
    y += 2
    hLine(doc, y - 1)
    y += 3
  }

  // 06 Regulatory
  y = sectionTitle(doc, '06', 'UAE reporting references', y, paintHeader)
  doc.setFontSize(8.5)
  setInk(doc, C.mute)
  y = textBlock(
    doc,
    'Preliminary and source-linked. “No trigger identified” means your answers did not activate a rule. It does not prove the reference is irrelevant.',
    MARGIN_X,
    y,
    CONTENT_W,
    4,
    paintHeader,
  )
  y += 5
  for (const item of result.applicability.slice(0, 10)) {
    y = ensure(doc, y, 18, paintHeader)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    setInk(doc, C.ink)
    y = textBlock(doc, item.title, MARGIN_X, y, CONTENT_W, 4, paintHeader)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    setInk(doc, C.accent)
    y = textBlock(doc, applicabilityLabel(item.state), MARGIN_X, y, CONTENT_W, 3.8, paintHeader)
    setInk(doc, C.mute)
    y = textBlock(
      doc,
      `${item.authority}  ·  Reviewed ${item.lastReviewed}`,
      MARGIN_X,
      y,
      CONTENT_W,
      3.6,
      paintHeader,
    )
    y = textBlock(doc, item.url, MARGIN_X, y, CONTENT_W, 3.6, paintHeader)
    y += 5
  }

  // 07 Action plan
  y = sectionTitle(doc, '07', '30 to 90 day action plan', y, paintHeader)
  result.recommendations.forEach((rec, i) => {
    y = ensure(doc, y, 26, paintHeader)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    setInk(doc, C.accent)
    doc.text(String(i + 1).padStart(2, '0'), MARGIN_X, y)
    setInk(doc, C.mute)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text(`${rec.priority.toUpperCase()}  ·  ${rec.timeframe} days`, MARGIN_X + 12, y)
    y += 5
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9.5)
    setInk(doc, C.ink)
    y = textBlock(doc, rec.action, MARGIN_X + 12, y, CONTENT_W - 12, 4.2, paintHeader)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    setInk(doc, C.mute)
    y = textBlock(doc, rec.whyRelevant, MARGIN_X + 12, y, CONTENT_W - 12, 3.8, paintHeader)
    setInk(doc, C.body)
    y = textBlock(
      doc,
      `Owner: ${rec.owner}  ·  Deliverable: ${rec.expectedDeliverable}`,
      MARGIN_X + 12,
      y,
      CONTENT_W - 12,
      3.8,
      paintHeader,
    )
    y += 3
    hLine(doc, y)
    y += 5
  })

  // 08 Methodology
  y = sectionTitle(doc, '08', 'How these results were prepared', y, paintHeader)
  const methodParas = [
    `Method version ${result.methodologyVersion}. Scores are calculated in the browser from the answers entered. They are not randomly generated.`,
    'What matters most: each topic was rated for business effect and for people/environment effect. These appear separately on the matrix.',
    'Carbon readiness reflects whether processes and records exist to compile an inventory later. It does not calculate emissions in tonnes. Only applicable sources are scored. Missing data is never treated as zero emissions.',
    'Not sure / Not relevant answers are excluded from averages so uncertainty stays visible.',
    'Evidence confidence is self-reported process maturity. Documents were not inspected or verified.',
    'UAE reporting references are preliminary and source-linked. They are not a legal determination of applicability.',
  ]
  for (const p of methodParas) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    setInk(doc, C.body)
    y = textBlock(doc, `-  ${p}`, MARGIN_X, y, CONTENT_W, 4.2, paintHeader)
    y += 2
  }

  // 09 Limitations
  y = sectionTitle(doc, '09', 'Limitations and next steps', y, paintHeader)
  doc.setFontSize(9)
  setInk(doc, C.body)
  y = textBlock(doc, data.disclaimer, MARGIN_X, y, CONTENT_W, 4.2, paintHeader)
  y += 4
  y = textBlock(doc, data.nextStepsNote, MARGIN_X, y, CONTENT_W, 4.2, paintHeader)
  y += 8
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  setInk(doc, C.accent)
  doc.text(data.brandName, MARGIN_X, y)
  y += 5
  doc.setFont('helvetica', 'normal')
  setInk(doc, C.body)
  doc.text(data.contactEmail, MARGIN_X, y)
  y += 5
  doc.setFontSize(8)
  setInk(doc, C.mute)
  doc.text('https://ecopshub.com/tools/uae-sustainability-assessment', MARGIN_X, y)

  // Chrome on body pages
  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    drawPageChrome(doc, data.brandName, company, i, total, i === 1)
  }

  return doc.output('blob')
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
